import { FrontendApi } from '@ory/kratos-client';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

import { useGlobalAuthApi } from '@r-client/shared/data/auth';

import { flowIsExpired } from '../../utils/shared/flow-utils';

const mapping = (globalAuthApi: FrontendApi) =>
  ({
    'login-flow': {
      get: globalAuthApi.getLoginFlow,
      create: globalAuthApi.createBrowserLoginFlow,
    },
    'registration-flow': {
      get: globalAuthApi.getRegistrationFlow,
      create: globalAuthApi.createBrowserRegistrationFlow,
    },
    'settings-flow': {
      get: globalAuthApi.getSettingsFlow,
      create: globalAuthApi.createBrowserSettingsFlow,
    },
    'recovery-flow': {
      get: globalAuthApi.getRecoveryFlow,
      create: globalAuthApi.createBrowserRecoveryFlow,
    },
    'verification-flow': {
      get: globalAuthApi.getVerificationFlow,
      create: globalAuthApi.createBrowserVerificationFlow,
    },
  } as const);

type OverrideQueryOptions<T> = Pick<Partial<UseQueryOptions<T>>, 'enabled'>;

export function getFlowQueryBuilder<T extends keyof ReturnType<typeof mapping>>(
  flowType: T
) {
  type Config = ReturnType<typeof mapping>[T];
  type Variables = Parameters<Config['create']>[0] & {
    flowId?: string;
    omitSessionCookieOnCreate?: boolean;
  };
  type Flow = Awaited<ReturnType<Config['get']>>['data'];
  return (
    variables: Variables = {},
    options: OverrideQueryOptions<Flow> = {}
  ) => {
    const globalAuthApi = useGlobalAuthApi();
    const globalAuthApiMethods = mapping(globalAuthApi)[flowType];
    const get = globalAuthApiMethods.get.bind(globalAuthApi);
    const create = globalAuthApiMethods.create.bind(globalAuthApi);

    const { flowId, omitSessionCookieOnCreate, ...createParams } = variables;

    return useQuery({
      queryKey: [
        'global-auth',
        flowType,
        flowId,
        createParams,
        omitSessionCookieOnCreate,
      ],
      meta: {
        reportingKey: ['global-auth', flowType],
        reportLevel: 'info',
      },
      retry: false,
      queryFn: async (): Promise<Flow> => {
        const requestConfig = omitSessionCookieOnCreate
          ? {
              headers: {
                'X-Omit-Session-Cookie': 'true',
              },
            }
          : undefined;

        if (flowId) {
          const existingFlow = await get({ id: flowId }, requestConfig);
          if (
            existingFlow &&
            !flowIsExpired(existingFlow.data, minutesToMilliseconds(2))
          ) {
            return existingFlow.data;
          }
        }

        const { data } = await create(createParams, requestConfig);
        return data;
      },
      ...options,
    });
  };
}

export const useGetLoginFlowQuery = getFlowQueryBuilder('login-flow');
export const useGetRegisterFlowQuery = getFlowQueryBuilder('registration-flow');
export const useGetSettingsFlowQuery = getFlowQueryBuilder('settings-flow');
export const useGetRecoveryFlowQuery = getFlowQueryBuilder('recovery-flow');
export const useGetVerificationFlowQuery =
  getFlowQueryBuilder('verification-flow');
